import { useMemo, useRef } from 'react';
import { NJText } from '@engie-group/fluid-design-system-react';
import type { ExpressionSpecification, LayerSpecification } from 'mapbox-gl';
import { UMMap, UMMapControls, UMR, type UMMapHandle } from '../../urbanmap/_components';
import { DEFAULT_VISIBILITY, planLayers, planSources } from '../proposition-c/mapLayers';
import { PLAN } from '../proposition-c/tokens';
import type { PlanGeoData } from '../proposition-c/types';
import { YEARS } from './engine';
import type { TechnicalPoint } from './types';
import { useMapInteraction } from './mapInteraction';

/** Ce que la carte superpose à la carte Gentilly de base, selon la famille / l'onglet actif. */
export interface MapOverlay {
  /** Plan de dev : bâtiments raccordés par le dernier calcul. */
  connected?: Set<string>;
  selectedBuildings?: string[];
  selectionBuildings?: string[];
  editAllPipes?: boolean;
  focusedBuildingId?: string;
  focusedPointId?: string;
  focusRequest?: number;
  technicalPoints?: TechnicalPoint[];
  highlightedBuildings?: string[];
  highlightedPipes?: number[];
  areaMode?: boolean;
  onAreaSelect?: (selection: { buildings: string[]; pipes: number[]; points: string[] }) => void;
  onAreaError?: (message: string) => void;
  onPipeClick?: (index: number) => void;
  onPointClick?: (id: string) => void;
  buildingPhases?: Record<string, string>;
  routePipeIndexes?: number[];
  routePipePhases?: Record<number, string>;
  /** Hypothèses tracé (mock) : N tronçons du réseau présentés comme routes interdites / obligatoires / pondérées. */
  forbiddenRoads?: number;
  mandatoryRoads?: number;
  weightedRoads?: number;
  /** Points de départ (mock) : N points de livraison présentés comme points de départ du réseau. */
  startPoints?: number;
  /** Importance des clients : bâtiments obligatoires (sélection). */
  mandatory?: string[];
  /** Clic sur un bâtiment (sélection des obligatoires). */
  onBuildingClick?: (fid: string) => void;
  legend: string[];
}

const EMPTY: Set<string> = new Set();
const fc = (features: GeoJSON.Feature[]): GeoJSON.FeatureCollection => ({ type: 'FeatureCollection', features });
const COLOR = { forbidden: '#d6322a', mandatory: '#1b9e3e', weighted: '#e8a100', start: '#1b9e3e', connected: PLAN.map.scenario, mandatoryBuilding: '#7a3fd1', planBuilding: '#00726d', planRoute: '#ef7d00' };

export function MapPane({ geo, overlay }: { geo: PlanGeoData; overlay: MapOverlay }) {
  const mapRef = useRef<UMMapHandle>(null);
  const mapError = useMapInteraction(mapRef, geo, overlay);
  const { connected = EMPTY, forbiddenRoads = 0, mandatoryRoads = 0, weightedRoads = 0, startPoints = 0, mandatory = [], selectedBuildings = [], focusedBuildingId, buildingPhases = {}, routePipeIndexes = [], routePipePhases = {}, onBuildingClick } = overlay;

  const sources = useMemo(() => {
    const base = planSources(geo, { modified: {}, drift: new Set(), connected });
    const pipes = geo.pipes.features;
    const a = forbiddenRoads;
    const b = forbiddenRoads + mandatoryRoads;
    return {
      ...base,
      'nav-lasso': fc([]),
      'nav-technical': fc((overlay.technicalPoints ?? []).map((point) => ({
        type: 'Feature', properties: { id: point.id, status: point.status },
        geometry: { type: 'Point', coordinates: point.coordinates },
      }))),
      'nav-highlight-buildings': fc(geo.buildings.features.filter((feature) =>
        overlay.highlightedBuildings?.includes(String(feature.properties?.functionalId)))),
      'nav-highlight-pipes': fc((overlay.highlightedPipes ?? []).flatMap((index) =>
        pipes[index] ? [pipes[index]] : [])),
      'nav-forbidden': fc(pipes.slice(0, a)),
      'nav-mandatory-roads': fc(pipes.slice(a, b)),
      'nav-weighted': fc(pipes.slice(b, b + weightedRoads)),
      'nav-start': fc(geo.deliveryPoints.features.slice(0, startPoints)),
      'nav-plan-route': fc(routePipeIndexes.flatMap((index) => pipes[index]
        ? [{ ...pipes[index], properties: { ...pipes[index].properties, pipeIndex: index } }] : [])),
      'nav-pipe-candidates': fc((overlay.editAllPipes ? pipes : routePipeIndexes.map((i) => pipes[i])).filter(Boolean).map((pipe) => ({
        ...pipe, properties: { ...pipe.properties, pipeIndex: pipes.indexOf(pipe) },
      }))),
      ...Object.fromEntries(YEARS.map((year) => [
        `nav-phase-pipes-${year}`,
        fc(routePipeIndexes.filter((index) => routePipePhases[index] === year).map((index) => pipes[index]).filter((feature): feature is GeoJSON.Feature => !!feature)),
      ])),
    };
  }, [geo, connected, forbiddenRoads, mandatoryRoads, weightedRoads, startPoints, routePipeIndexes, routePipePhases, overlay.technicalPoints, overlay.highlightedBuildings, overlay.highlightedPipes, overlay.editAllPipes]);

  const layers = useMemo<LayerSpecification[]>(() => [
    ...planLayers({ ...DEFAULT_VISIBILITY, roads: false, zone: false, delivery: startPoints === 0 }, { pickedId: null, scenarioShown: false, perimeterOnly: true }),
    { id: 'nav-perimeter', type: 'line', source: 'perimeter', paint: { 'line-color': PLAN.map.perimeter, 'line-width': 2, 'line-dasharray': [2, 1.5] } },
    { id: 'nav-connected', type: 'fill', source: 'buildings', filter: ['==', ['get', 'connected'], 1], paint: { 'fill-color': COLOR.connected, 'fill-opacity': connected.size ? 0.75 : 0 } },
    { id: 'nav-forbidden', type: 'line', source: 'nav-forbidden', paint: { 'line-color': COLOR.forbidden, 'line-width': 6, 'line-dasharray': [1.2, 1.2] } },
    { id: 'nav-mandatory-roads', type: 'line', source: 'nav-mandatory-roads', paint: { 'line-color': COLOR.mandatory, 'line-width': 6 } },
    { id: 'nav-weighted', type: 'line', source: 'nav-weighted', paint: { 'line-color': COLOR.weighted, 'line-width': 6, 'line-opacity': 0.85 } },
    { id: 'nav-start', type: 'circle', source: 'nav-start', paint: { 'circle-color': COLOR.start, 'circle-radius': 9, 'circle-stroke-color': '#fff', 'circle-stroke-width': 3 } },
    {
      id: 'nav-mandatory', type: 'line', source: 'buildings', filter: ['in', ['get', 'functionalId'], ['literal', mandatory]],
      paint: { 'line-color': COLOR.mandatoryBuilding, 'line-width': 4, 'line-opacity': mandatory.length ? 1 : 0 },
    },
    {
      id: 'nav-selection', type: 'fill', source: 'buildings',
      filter: ['all', ['in', ['get', 'functionalId'], ['literal', overlay.selectionBuildings ?? []]],
        ['!', ['in', ['get', 'functionalId'], ['literal', selectedBuildings]]]],
      paint: { 'fill-color': COLOR.mandatoryBuilding, 'fill-opacity': 0.65 },
    },
    {
      id: 'nav-plan-selected', type: 'fill', source: 'buildings', filter: ['in', ['get', 'functionalId'], ['literal', selectedBuildings]],
      paint: { 'fill-color': COLOR.planBuilding, 'fill-opacity': selectedBuildings.length ? 0.38 : 0 },
    },
    {
      id: 'nav-plan-focused', type: 'line', source: 'buildings', filter: ['==', ['get', 'functionalId'], focusedBuildingId ?? ''],
      paint: { 'line-color': COLOR.connected, 'line-width': 5, 'line-opacity': focusedBuildingId ? 1 : 0 },
    },
    {
      id: 'nav-plan-phase', type: 'line', source: 'buildings',
      filter: ['in', ['get', 'functionalId'], ['literal', Object.keys(buildingPhases)]],
      paint: { 'line-color': Object.keys(buildingPhases).length
        ? ['match', ['get', 'functionalId'], ...Object.entries(buildingPhases).flatMap(([id, year]) => [id, Number(year) % 3 === 0 ? '#00726d' : Number(year) % 3 === 1 ? '#2176ae' : '#c05621']), '#00726d'] as ExpressionSpecification
        : COLOR.planBuilding, 'line-width': 3 },
    },
    { id: 'nav-plan-route', type: 'line', source: 'nav-plan-route', paint: { 'line-color': COLOR.planRoute, 'line-width': 5, 'line-opacity': 0.95 } },
    ...YEARS.map((year) => ({
      id: `nav-phase-pipes-${year}`,
      type: 'line' as const,
      source: `nav-phase-pipes-${year}`,
      paint: { 'line-color': Number(year) % 3 === 0 ? '#00726d' : Number(year) % 3 === 1 ? '#2176ae' : '#c05621', 'line-width': 6 },
    })),
    { id: 'nav-highlight-buildings', type: 'line', source: 'nav-highlight-buildings', paint: { 'line-color': '#de167b', 'line-width': 5 } },
    { id: 'nav-highlight-pipes', type: 'line', source: 'nav-highlight-pipes', paint: { 'line-color': '#de167b', 'line-width': 8 } },
    { id: 'nav-pipe-hit', type: 'line', source: 'nav-pipe-candidates', paint: { 'line-color': '#ef7d00', 'line-width': 18, 'line-opacity': 0.01 } },
    { id: 'nav-technical', type: 'circle', source: 'nav-technical', paint: {
      'circle-radius': 8, 'circle-color': ['match', ['get', 'status'], 'validated', '#00726d', '#ef7d00'],
      'circle-stroke-color': '#fff', 'circle-stroke-width': 2,
    } },
    { id: 'nav-technical-focus', type: 'circle', source: 'nav-technical', filter: ['==', ['get', 'id'], overlay.focusedPointId ?? ''], paint: {
      'circle-radius': 12, 'circle-color': '#de167b', 'circle-opacity': 0.25, 'circle-stroke-color': '#de167b', 'circle-stroke-width': 2,
    } },
    { id: 'nav-lasso', type: 'line', source: 'nav-lasso', paint: { 'line-color': '#de167b', 'line-width': 3 } },
  ], [connected, startPoints, mandatory, selectedBuildings, focusedBuildingId, buildingPhases, routePipePhases, overlay.focusedPointId, overlay.selectionBuildings]);

  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0, height: '100%' }}>
      <UMMap
        ref={mapRef}
        sources={sources}
        layers={layers}
        initialBounds={geo.bounds}
        interactiveLayerIds={overlay.areaMode ? [] : [
          ...(overlay.onPointClick ? ['nav-technical'] : []),
          ...(overlay.onPipeClick ? ['nav-pipe-hit'] : []),
          ...(onBuildingClick ? ['plan-building-fill'] : []),
        ]}
        onFeatureClick={(layer, f) => {
          if (layer === 'nav-technical') overlay.onPointClick?.(String(f.properties?.id));
          else if (layer === 'nav-pipe-hit') overlay.onPipeClick?.(Number(f.properties?.pipeIndex));
          else onBuildingClick?.(String(f.properties?.functionalId));
        }}
      />
      <UMMapControls basemap="light" availableBasemaps={['light']} onZoomIn={() => mapRef.current?.zoomIn()} onZoomOut={() => mapRef.current?.zoomOut()} />
      {mapError && <div style={{ position: 'absolute', top: 48, left: 12, right: 12, zIndex: 4, background: UMR.white, padding: 8 }}>
        <NJText role="alert" scale="xs">{mapError}</NJText>
      </div>}
      <div style={{ position: 'absolute', left: 12, bottom: 12, zIndex: 10, background: UMR.white, boxShadow: '0 1px 6px rgba(0,0,0,0.2)', padding: '8px 12px', fontFamily: UMR.font, fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 360 }}>
        {overlay.legend.map((l) => <span key={l}>{l}</span>)}
      </div>
    </div>
  );
}
